// aiiop Fabric - LandingPage v5
import { useState, useRef, useEffect } from 'react';
import '../styles/landing.css';

/* ── Request Access Modal ─────────────────────────────────────────────────── */
function RequestAccessModal({ onClose }) {
  const [step,     setStep]    = useState('form');   // form | success
  const [captcha,  setCaptcha] = useState(null);     // { token, question }
  const [answer,   setAnswer]  = useState('');
  const [form,     setForm]    = useState({ full_name:'', email:'', company:'', use_case:'' });
  const [saving,   setSaving]  = useState(false);
  const [err,      setErr]     = useState(null);
  const [loadingC, setLoadingC] = useState(true);

  const BASE = window.location.origin;

  const loadCaptcha = async () => {
    setLoadingC(true);
    setAnswer('');
    try {
      const r = await fetch(`${BASE}/api/v1/leads/captcha`);
      if (!r.ok) throw new Error('Captcha unavailable');
      setCaptcha(await r.json());
    } catch(e) {
      setErr('Could not load captcha. Please try again.');
    } finally {
      setLoadingC(false);
    }
  };

  // Load captcha on mount
  useEffect(() => { loadCaptcha(); }, []); // eslint-disable-line

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const submit = async () => {
    if (!form.full_name || !form.email) { setErr('Name and email are required.'); return; }
    if (!captcha) { setErr('Please wait for the captcha to load.'); return; }
    if (!answer.trim()) { setErr('Please answer the captcha.'); return; }
    setSaving(true); setErr(null);
    try {
      const r = await fetch(`${BASE}/api/v1/leads`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          ...form,
          captcha_token:  captcha.token,
          captcha_answer: answer.trim(),
        }),
      });
      const data = await r.json();
      if (!r.ok) throw new Error(data.detail || 'Submission failed.');
      setStep('success');
    } catch(e) {
      setErr(e.message);
      // Refresh captcha on any error (token is single-use)
      loadCaptcha();
    } finally {
      setSaving(false);
    }
  };

  return (
    <div style={{
      position:'fixed', inset:0, background:'rgba(0,0,0,.75)',
      display:'flex', alignItems:'center', justifyContent:'center',
      zIndex:1000, padding:'16px',
      overflowY:'auto',
    }} onClick={onClose}>
      <div style={{
        background:'#151515', border:'1px solid #2a2a2a', borderRadius:12,
        padding:'28px 32px', width:'100%', maxWidth:460, position:'relative',
        margin:'auto',
      }} onClick={e => e.stopPropagation()}>
        <button onClick={onClose} style={{
          position:'absolute', top:16, right:16, background:'none',
          border:'none', color:'#888', fontSize:20, cursor:'pointer', lineHeight:1,
        }}>×</button>

        {step === 'success' ? (
          <div style={{ textAlign:'center', padding:'12px 0 16px' }}>
            <div style={{ fontSize:40, marginBottom:16 }}>✓</div>
            <h3 style={{ color:'#fff', margin:'0 0 12px', fontSize:20 }}>We'll be in touch</h3>
            <p style={{ color:'#aaa', fontSize:14, lineHeight:1.6 }}>
              Thanks for your interest in Fabric. A member of our team will reach out
              to you shortly to discuss your use case and schedule a demo.
            </p>
            <button className="btn btn-primary" style={{ marginTop:16 }} onClick={onClose}>
              Close
            </button>
          </div>
        ) : (
          <>
            <div style={{ marginBottom:24 }}>
              <div style={{
                fontSize:10, fontWeight:700, letterSpacing:'.08em',
                textTransform:'uppercase', color:'#E8470A', marginBottom:8,
              }}>Request Access</div>
              <h3 style={{ color:'#fff', margin:0, fontSize:20 }}>Get started with Fabric</h3>
              <p style={{ color:'#888', fontSize:13, marginTop:8, lineHeight:1.5 }}>
                Tell us a bit about your use case. Our team will reach out to set up a demo.
              </p>
            </div>

            {err && (
              <div style={{
                background:'rgba(232,71,10,.1)', border:'1px solid rgba(232,71,10,.3)',
                borderRadius:6, padding:'10px 14px', fontSize:12,
                color:'#fb923c', marginBottom:16,
              }}>{err}</div>
            )}

            <div style={{ display:'flex', flexDirection:'column', gap:10 }}>
              <div>
                <label style={{ fontSize:11, color:'#888', display:'block', marginBottom:5,
                  fontWeight:600, letterSpacing:'.05em', textTransform:'uppercase' }}>
                  Full name *
                </label>
                <input
                  value={form.full_name} onChange={e => set('full_name', e.target.value)}
                  placeholder="Jane Smith"
                  style={{ width:'100%', padding:'9px 12px', background:'#0d0d0d',
                    border:'1px solid #2a2a2a', borderRadius:6, color:'#fff',
                    fontSize:13, outline:'none', boxSizing:'border-box' }}
                />
              </div>
              <div>
                <label style={{ fontSize:11, color:'#888', display:'block', marginBottom:5,
                  fontWeight:600, letterSpacing:'.05em', textTransform:'uppercase' }}>
                  Work email *
                </label>
                <input
                  type="email" value={form.email} onChange={e => set('email', e.target.value)}
                  placeholder="jane@company.com"
                  style={{ width:'100%', padding:'9px 12px', background:'#0d0d0d',
                    border:'1px solid #2a2a2a', borderRadius:6, color:'#fff',
                    fontSize:13, outline:'none', boxSizing:'border-box' }}
                />
              </div>
              <div>
                <label style={{ fontSize:11, color:'#888', display:'block', marginBottom:5,
                  fontWeight:600, letterSpacing:'.05em', textTransform:'uppercase' }}>
                  Company
                </label>
                <input
                  value={form.company} onChange={e => set('company', e.target.value)}
                  placeholder="Acme Corp"
                  style={{ width:'100%', padding:'9px 12px', background:'#0d0d0d',
                    border:'1px solid #2a2a2a', borderRadius:6, color:'#fff',
                    fontSize:13, outline:'none', boxSizing:'border-box' }}
                />
              </div>
              <div>
                <label style={{ fontSize:11, color:'#888', display:'block', marginBottom:5,
                  fontWeight:600, letterSpacing:'.05em', textTransform:'uppercase' }}>
                  What are you looking to train?
                </label>
                <textarea
                  value={form.use_case} onChange={e => set('use_case', e.target.value)}
                  placeholder="e.g. classify ITSM tickets, predict churn, extract entities from contracts…"
                  rows={3}
                  style={{ width:'100%', padding:'9px 12px', background:'#0d0d0d',
                    border:'1px solid #2a2a2a', borderRadius:6, color:'#fff',
                    fontSize:13, outline:'none', boxSizing:'border-box',
                    resize:'vertical', fontFamily:'inherit' }}
                />
              </div>

              {/* Server-side math captcha */}
              <div style={{
                background:'#0d0d0d', border:'1px solid #2a2a2a',
                borderRadius:6, padding:'14px',
              }}>
                <div style={{ fontSize:11, color:'#888', marginBottom:10,
                  fontWeight:600, letterSpacing:'.05em', textTransform:'uppercase' }}>
                  Quick check
                </div>
                {loadingC ? (
                  <div style={{ color:'#555', fontSize:13 }}>Loading…</div>
                ) : captcha ? (
                  <div style={{ display:'flex', alignItems:'center', gap:12 }}>
                    <span style={{
                      fontFamily:'var(--mono, monospace)', fontSize:16,
                      color:'#E8470A', fontWeight:700, minWidth:120,
                    }}>{captcha.question}</span>
                    <input
                      type="number" value={answer} onChange={e => setAnswer(e.target.value)}
                      placeholder="answer"
                      style={{ width:80, padding:'7px 10px', background:'#151515',
                        border:'1px solid #333', borderRadius:6, color:'#fff',
                        fontSize:14, outline:'none', textAlign:'center' }}
                    />
                    <button onClick={loadCaptcha} style={{
                      background:'none', border:'none', color:'#555',
                      fontSize:16, cursor:'pointer', padding:4,
                    }} title="New question">↺</button>
                  </div>
                ) : null}
              </div>

              <button
                className="btn btn-primary"
                onClick={submit}
                disabled={saving}
                style={{ width:'100%', padding:'11px', fontSize:14 }}
              >
                {saving ? 'Submitting…' : 'Request Access →'}
              </button>

              <p style={{ fontSize:11, color:'#555', textAlign:'center', margin:0 }}>
                No spam. We'll reach out personally within 1-2 business days.
              </p>
            </div>
          </>
        )}
      </div>
    </div>
  );
}



export default function LandingPage({ onGetStarted, onGoToDocs }) {
  const [view, setView] = useState('platform');
  const [showAccess, setShowAccess] = useState(false);
  const platformRef = useRef(null);
  const whyRef      = useRef(null);
  const heroRef     = useRef(null);

  const switchView = (v) => {
    setView(v);
    // Scroll to the relevant section after state update
    setTimeout(() => {
      const ref = v === 'platform' ? platformRef : whyRef;
      ref.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, 50);
  };

  const scrollToId = (view, id) => {
    switchView(view);
    setTimeout(() => document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' }), 80);
  };

  return (
    <div className="landing-page landing-wrap">

      <nav className="nav">
        <div className="nav-inner">
          <div className="logo" onClick={() => heroRef.current?.scrollIntoView({ behavior: 'smooth' })} style={{ cursor:'pointer' }}>
            <span>aiiop</span> Fabric<em>by aiiop.ai</em>
          </div>
          <div className="nav-links">
            <button className="nav-link" onClick={() => switchView('platform')}>Platform</button>
            <button className="nav-link" onClick={() => switchView('why')}>Why Fabric</button>
            <button className="nav-link" onClick={() => scrollToId('platform', 'governance')}>Governance</button>
            <button className="nav-link" onClick={() => scrollToId('why', 'usecases')}>Use Cases</button>
            <button className="nav-link" onClick={onGoToDocs}>User Guide</button>
          </div>
          <div className="nav-right">
            <a href="https://www.aiiop.ai" target="_blank" rel="noopener noreferrer" className="btn btn-ghost" style={{fontSize:'10px'}}>aiiop.ai</a>
            <button className="btn btn-ghost btn-outline-or" onClick={() => setShowAccess(true)}
              style={{ borderColor:'var(--or)', color:'var(--or)' }}>
              Request Access
            </button>
            <button className="btn btn-primary" onClick={onGetStarted}>Login →</button>
          </div>
        </div>
      </nav>

      {/* ── Hero ── */}
      <div style={{ paddingTop: 56 }}>
      <section className="hero" ref={heroRef}>
        <div className="container">
          <div className="hero-grid">
            <div>
              <div className="hero-pill">AI/ML Training Platform · v2.0</div>
              <h1>
                Your data has answers.<br/>
                Turn it into AI models<br/>
                <span className="or">without it ever leaving your network.</span>
              </h1>
              <p className="hero-sub">
                Fabric turns the data your business already has, spreadsheets, documents,
                ticket histories, into working AI models, trained on servers you control.
                Every model traceable, every run auditable, ready for the day a customer
                or regulator asks how it was built.
              </p>
              <div className="hero-btns">
                <button className="btn btn-primary btn-lg" onClick={onGetStarted}>Get Started →</button>
                <button className="btn btn-ghost btn-lg" onClick={() => setShowAccess(true)}
                  style={{ borderColor:'var(--or)', color:'var(--or)' }}>
                  Request Access
                </button>
                <button className="btn btn-ghost btn-lg" onClick={() => switchView('platform')}>How It Works</button>
                <button className="btn btn-ghost btn-lg" onClick={() => switchView('why')}>Why Fabric</button>
              </div>
              <div className="hero-stats">
                <div className="stat-item"><strong>0</strong><span>bytes sent externally</span></div>
                <div className="stat-item"><strong>5</strong><span>training modes</span></div>
                <div className="stat-item"><strong>23</strong><span>model families</span></div>
              </div>
            </div>
            <div className="term">
              <div className="term-bar">
                <div className="dot" style={{background:'#E8470A'}}/>
                <div className="dot" style={{background:'#2a2a2a'}}/>
                <div className="dot" style={{background:'#2a2a2a'}}/>
                <span className="term-label">Fabric - run #38 · Domain Pre-training</span>
              </div>
              <div className="term-body">
                <div className="t-section">Run</div>
                <div className="t-row"><span className="t-key">purpose</span><span className="t-val orange">DOMAIN_PRETRAIN</span></div>
                <div className="t-row"><span className="t-key">corpus</span><span className="t-val white">itsm_tickets_250k.csv</span></div>
                <div className="t-row"><span className="t-key">model</span><span className="t-val white">distilbert-base-uncased</span></div>
                <div className="t-section">Result</div>
                <div className="t-row"><span className="t-key">mlm_loss</span><span className="t-val green">0.3812 ↓</span></div>
                <div className="t-row"><span className="t-key">perplexity</span><span className="t-val green">1.464</span></div>
                <div className="t-row"><span className="t-key">status</span><span className="t-val green">● completed</span></div>
                <div className="t-section">Artifact</div>
                <div className="t-row"><span className="t-key">registered</span><span className="t-val green">model_registry ✓</span></div>
                <div className="t-row"><span className="t-key">next_run</span><span className="t-val orange">base model → run #39</span></div>
              </div>
            </div>
          </div>
        </div>
      </section>


      {/* ════════════════════════════════════════════════════════════════════
          PLATFORM
      ════════════════════════════════════════════════════════════════════ */}
      <div ref={platformRef} style={{ scrollMarginTop: 64 }}/>
      {view === 'platform' && (
        <>
          <section className="section" id="how-it-works">
            <div className="container">
              <div className="section-header centered">
                <div className="eyebrow" style={{justifyContent:'center'}}>How It Works</div>
                <h2>Your team. Your servers. Fabric in the middle.</h2>
                <p>Fabric sits between your team and your infrastructure. You define what gets trained and on what data. Training runs on servers you control. Artifacts come back registered and versioned.</p>
              </div>

              {/* Architecture diagram */}
              <div className="arch-diagram">
                {/* Row 1: Team → Fabric */}
                <div className="arch-row arch-row--top">
                  <div className="arch-actor">
                    <div className="arch-actor-icon">👤</div>
                    <div className="arch-actor-label">Your team</div>
                    <div className="arch-actor-sub">engineers, data scientists</div>
                  </div>
                  <div className="arch-arrow-h">
                    <div className="arch-arrow-line"/>
                    <div className="arch-arrow-labels">
                      <span>dataset</span>
                      <span>feature config</span>
                      <span>run parameters</span>
                    </div>
                  </div>
                  <div className="arch-core">
                    <div className="arch-core-label">aiiop Fabric</div>
                    <div className="arch-core-items">
                      <span>run management</span>
                      <span>audit log</span>
                      <span>model registry</span>
                      <span>artifact versioning</span>
                    </div>
                  </div>
                  <div className="arch-arrow-h arch-arrow-h--right">
                    <div className="arch-arrow-line"/>
                    <div className="arch-arrow-labels">
                      <span>metrics</span>
                      <span>logs</span>
                      <span>artifacts</span>
                    </div>
                  </div>
                  <div className="arch-actor">
                    <div className="arch-actor-icon">📦</div>
                    <div className="arch-actor-label">Model registry</div>
                    <div className="arch-actor-sub">versioned, exportable</div>
                  </div>
                </div>

                {/* Vertical arrow down from Fabric */}
                <div className="arch-row arch-row--mid">
                  <div className="arch-spacer"/>
                  <div className="arch-arrow-v">
                    <div className="arch-arrow-v-line"/>
                    <div className="arch-arrow-v-label">training runs on your servers</div>
                  </div>
                  <div className="arch-spacer"/>
                </div>

                {/* Row 2: Your servers */}
                <div className="arch-row arch-row--bottom">
                  {[
                    {icon:'🖥️', label:'On-premise server', sub:'CPU or GPU'},
                    {icon:'☁️', label:'Cloud VM', sub:'any provider'},
                    {icon:'⚡', label:'GPU cluster', sub:'for large models'},
                  ].map(s => (
                    <div key={s.label} className="arch-server">
                      <div className="arch-server-icon">{s.icon}</div>
                      <div className="arch-server-label">{s.label}</div>
                      <div className="arch-server-sub">{s.sub}</div>
                      <div className="arch-server-note">data never leaves</div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Six steps - concise, no implementation detail */}
              <div className="journey-steps" style={{marginTop:56}}>
                {[
                  ['01','Register a server', 'Point Fabric at a server you own. Fabric verifies it can reach it and sets up the execution environment.'],
                  ['02','Pick a training mode', 'Classification, regression, domain pre-training, NER or custom. The mode shapes everything downstream.'],
                  ['03','Register your dataset', 'Upload a file or point to one already on your server. Fabric profiles every column before anything runs.'],
                  ['04','Configure what the model learns', 'Assign roles to columns. Approve the config to lock it. The snapshot is stored permanently with the run.'],
                  ['05','Run training', 'Training executes on your server. Logs refresh every 10 seconds during training. Metrics collected on completion.'],
                  ['06','Export the artifact', 'The trained model is versioned and registered. Domain pre-training outputs are available as base models for subsequent runs. Export to your inference environment when ready.'],
                ].map(([n, title, desc]) => (
                  <div key={n} className="j-step">
                    <div className="j-num">step {n}</div>
                    <h4>{title}</h4>
                    <p>{desc}</p>
                  </div>
                ))}
              </div>
            </div>
          </section>

          <section className="section section-alt" id="purposes">
            <div className="container">
              <div className="section-header centered">
                <div className="eyebrow" style={{justifyContent:'center'}}>Training Modes</div>
                <h2>One platform, every kind of model your business needs.</h2>
                <p>Pick what you want the model to do. Fabric handles the rest: the right model choices, the right data checks, and results your team can compare and trust.</p>
              </div>
              <div className="purposes-grid">
                {[
                  {icon:'🎯',badge:'CLASSIFICATION',cls:'p-class',title:'Classification',
                    desc:'Sort things automatically: route support tickets to the right team, flag risky transactions, triage incoming requests. Decisions your staff make by hand today, made instantly and consistently.',
                    families:'BERT · Classical ML · Embeddings'},
                  {icon:'🧠',badge:'DOMAIN_PRETRAIN',cls:'p-dapt',title:'Domain Pre-training',
                    desc:'Teach AI your industry\'s language from your own manuals, tickets and documents. Upload PDFs and Word files, Fabric turns them into a training corpus. Every model you build afterwards understands your terminology, not generic internet text.',
                    families:'BERT family', featured:true},
                  {icon:'📈',badge:'REGRESSION',cls:'p-reg',title:'Regression',
                    desc:'Predict numbers that matter: prices, demand, delivery times, resource load. Forecasts grounded in your own history instead of gut feel.',
                    families:'Classical ML · SVM'},
                  {icon:'🏷️',badge:'NER',cls:'p-ner',title:'Named Entity Recognition',
                    desc:'Pull names, dates, amounts and clauses out of contracts, emails and forms automatically. Hours of manual reading turned into structured data in seconds.',
                    families:'BERT family'},
                  {icon:'⚙️',badge:'CUSTOM',cls:'p-custom',title:'Custom',
                    desc:'Your data science team has its own methods? Bring them. Fabric runs your scripts with the same governance, tracking and audit trail as everything else.',
                    families:'All families'},
                ].map(p => (
                  <div key={p.badge} className={`purpose-card ${p.featured ? 'purpose-card--featured' : ''}`}>
                    <div className="purpose-icon">{p.icon}</div>
                    <div className={`purpose-badge ${p.cls}`}>{p.badge}</div>
                    <h4>{p.title}</h4>
                    <p>{p.desc}</p>
                    <div className="purpose-families">{p.families}</div>
                  </div>
                ))}
              </div>
            </div>
          </section>

          <section className="section" id="pipeline">
            <div className="container">
              <div className="section-header centered">
                <div className="eyebrow" style={{justifyContent:'center'}}>Pipelines</div>
                <h2>Fabric fits your process, not the other way round.</h2>
                <p>Build a pipeline template once and reuse it across runs. Each stage is a script you upload and version independently.</p>
              </div>
              <div className="pipe-row">
                {[
                  {badge:'PREPROCESS',cls:'p-preprocess',title:'Pre-processing',
                    desc:'Runs before training. Use it for text cleaning, noise removal, custom encodings or anything else you want applied before the training loop sees the data.'},
                  {badge:'TRAIN',cls:'p-train',title:'Training',
                    desc:'Generated automatically for the five standard modes. For custom runs, swap in your own script here.'},
                  {badge:'EVALUATE',cls:'p-evaluate',title:'Evaluation',
                    desc:'Runs after training. Write your own evaluation logic and return a metrics dict - it gets stored alongside the standard metrics.'},
                  {badge:'POSTPROCESS',cls:'p-postprocess',title:'Post-processing',
                    desc:'Threshold adjustment, calibration, output reshaping. Runs after evaluation.'},
                ].map(p => (
                  <div key={p.badge} className="pipe-card">
                    <span className={`pipe-badge ${p.cls}`}>{p.badge}</span>
                    <h5>{p.title}</h5>
                    <p>{p.desc}</p>
                    <div className="pipe-opt">optional</div>
                  </div>
                ))}
              </div>
              <div className="pipe-props">
                {[
                  ['🔐','Tamper-proof','Each plugin is checksummed before execution. If the file changes, the run won\'t start.'],
                  ['🔬','Isolated execution','Plugins run independently. A crash in your script doesn\'t affect other runs or the platform.'],
                  ['♻️','Version-pinned templates','Create a template once. Scripts are pinned by version - no surprises on re-runs.'],
                ].map(([icon,title,desc]) => (
                  <div key={title} className="pipe-prop">
                    <div className="prop-icon">{icon}</div>
                    <div className="prop-body"><strong>{title}</strong><span>{desc}</span></div>
                  </div>
                ))}
              </div>
            </div>
          </section>

          <section className="section section-alt" id="models">
            <div className="container">
              <div className="section-header">
                <div className="eyebrow">Models</div>
                <h2>You do not need to know the model names. Fabric does.</h2>
                <p>They use different feature handling, different training scripts and produce different metrics. Models load from a local cache on your execution server - nothing is downloaded from the internet at training time.</p>
              </div>
              <div className="model-paths">
                <div className="model-path">
                  <div className="model-path-header">
                    <span className="model-path-badge">Transformer path</span>
                    <span className="model-path-sub">neural language models</span>
                  </div>
                  <div className="model-path-grid">
                    {[
                      {badge:'recommended', title:'DistilBERT',
                        desc:'60% smaller than BERT Base, 97% of the performance. Trains on CPU in reasonable time. Good starting point.',
                        tag:'lightweight · CPU-friendly'},
                      {title:'BERT Base / RoBERTa',
                        desc:'Larger encoders with stronger contextual representations. Use when you need the extra accuracy and have more compute.',
                        tag:'larger · higher accuracy'},
                      {title:'ALBERT / DeBERTa',
                        desc:'Parameter-sharing and disentangled attention architectures. DeBERTa-v3 often beats RoBERTa on classification tasks.',
                        tag:'parameter-efficient'},
                      {title:'LLM family',
                        desc:'Mistral 7B, LLaMA 3, Phi-3, Qwen 2.5, Gemma 2, DeepSeek R1, Qwen2.5-Coder, Kimi K2. Available for custom fine-tuning. GPU server required.',
                        tag:'GPU server required'},
                    ].map(m => (
                      <div key={m.title} className="model-card">
                        {m.badge && <div className="model-badge">{m.badge}</div>}
                        <h4>{m.title}</h4>
                        <p>{m.desc}</p>
                        <div className="model-tag">{m.tag}</div>
                      </div>
                    ))}
                  </div>
                </div>
                <div className="model-path">
                  <div className="model-path-header">
                    <span className="model-path-badge model-path-badge--classical">Classical ML path</span>
                    <span className="model-path-sub">tabular and text feature models</span>
                  </div>
                  <div className="model-path-grid">
                    {[
                      {badge:'recommended', title:'XGBoost / LightGBM',
                        desc:'Gradient boosted trees. Fast to train, good on tabular data with text features, and easy to reason about.',
                        tag:'fast · tabular + text features'},
                      {title:'Random Forest',
                        desc:'Good baseline. Less sensitive to hyperparameters than boosted trees and handles messy data reasonably well.',
                        tag:'robust · good baseline'},
                      {title:'Logistic Regression / SVM',
                        desc:'Linear models. Fast, predictable, and straightforward to audit. Useful when you want something you can explain.',
                        tag:'linear · interpretable'},
                    ].map(m => (
                      <div key={m.title} className="model-card">
                        {m.badge && <div className="model-badge">{m.badge}</div>}
                        <h4>{m.title}</h4>
                        <p>{m.desc}</p>
                        <div className="model-tag">{m.tag}</div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </section>

          <section className="section" id="governance">
            <div className="container">
              <div className="section-header centered">
                <div className="eyebrow" style={{justifyContent:'center'}}>Audit & Access</div>
                <h2>Who did what, with which data, when.</h2>
                <p>Every write to the system is logged with the user, org and timestamp. The feature config and model reference used for a run are frozen when the run is created - not when it completes.</p>
              </div>
              <div className="gov-grid">
                <div className="gov-audit">
                  <div className="audit-head"><span>audit log</span></div>
                  {[
                    ['RUN_COMPLETED · run #38 · DOMAIN_PRETRAIN','system','a-ok'],
                    ['DAPT_ARTIFACT_REGISTERED · distilbert-itsm','system','a-ok'],
                    ['RUN_STARTED · run #39 · CLASSIFICATION','engineer@aiiop.ai','a-run'],
                    ['CONFIG_APPROVED · itsm_v3 · CLASSIFICATION','admin@aiiop.ai','a-ok'],
                  ].map(([action,user,cls]) => (
                    <div key={action} className="audit-row">
                      <span className="audit-action">{action}</span>
                      <span className="audit-user">{user}</span>
                      <span className={`audit-status ${cls}`}>{cls==='a-ok'?'ok':'running'}</span>
                    </div>
                  ))}
                </div>
                <div className="gov-cards">
                  {[
                    ['🔒','Your data stays put','Training runs on your servers. Your data never leaves your network.'],
                    ['📋','Audit log','User, org, timestamp on every write. You can see who approved a config, who started a run, when.'],
                    ['👥','Role-based access','Four roles: Admin, ML Engineer, Data Engineer, Viewer. Role changes take effect on the user\'s next request, no re-login required.'],
                    ['🏢','Tenant isolation','Every database query is scoped to the organisation. One tenant cannot see another\'s data.'],
                    ['🔑','Secrets management','Credentials are stored in a dedicated secrets vault, never in the application database.'],
                    ['📸','Run snapshots','The data config and model reference for a run are snapshotted at creation time. You can reproduce any run exactly.'],
                  ].map(([icon,title,desc]) => (
                    <div key={title} className="gov-card">
                      <div className="gov-icon">{icon}</div>
                      <h5>{title}</h5>
                      <p>{desc}</p>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </section>
        </>
      )}

      {/* ════════════════════════════════════════════════════════════════════
          WHY FABRIC
      ════════════════════════════════════════════════════════════════════ */}
      <div ref={whyRef} style={{ scrollMarginTop: 64 }}/>
      {view === 'why' && (
        <>
          <section className="section section-alt">
            <div className="container">
              <div className="section-header centered">
                <div className="eyebrow" style={{justifyContent:'center'}}>The Problem</div>
                <h2>ML teams are shipping models they can't explain or reproduce.</h2>
                <p style={{color:'#334155',marginTop:8}}>
                  Fabric was built to answer those questions by design, not by documentation.
                </p>
              </div>
              <div className="pain-list">
                {[
                  ['⚡','Which run used which data?',
                    'Six months after you trained a model, can you tell an auditor exactly which dataset version it was trained on, which columns were included, and which parameters were used? For most teams the honest answer is no.'],
                  ['🔒','Your data can\'t go to a third-party API.',
                    'GDPR, HIPAA, internal data policies, competitive sensitivity. Cloud ML APIs are fast and convenient - until they aren\'t an option. If you\'re training on patient records, financial transactions or anything sensitive, you need to run it yourself.'],
                  ['📋','A regulator is asking questions.',
                    'Which training data was used for the model that produced that output? Who approved it? When was it last retrained? These are real questions that real teams are being asked. "It\'s in a notebook somewhere" is not an acceptable answer.'],
                  ['🎯','Every ML project starts from scratch.',
                    'No standard way to hand off a model to a colleague. No shared pipeline. No reusable feature configs. Each project is its own bespoke thing, which means every new engineer has to figure out what the last one did.'],
                  ['👥','Key knowledge walks out the door.',
                    'When the person who built the model leaves, so does the understanding of what went into it. Which preprocessing decisions, why those columns, what the model can and can\'t handle.'],
                  ['💰','You\'re paying for servers you\'re not using.',
                    'Most organisations already have compute - VMs, on-prem hardware, a GPU box collecting dust. Cloud ML adds cost and data movement on top of infrastructure you\'re already paying for.'],
                ].map(([icon,title,desc]) => (
                  <div key={title} className="pain-item">
                    <div className="pain-ico">{icon}</div>
                    <div className="pain-text"><strong>{title}</strong><span>{desc}</span></div>
                  </div>
                ))}
              </div>
            </div>
          </section>

          <section className="section">
            <div className="container">
              <div className="section-header centered">
                <div className="eyebrow" style={{justifyContent:'center'}}>What Fabric Does</div>
                <h2>A training workflow your whole organisation can trust.</h2>
                <p>Not a notebook replacement. A layer on top of your infrastructure that makes training runs traceable, repeatable and exportable.</p>
              </div>
              <div className="benefit-grid">
                {[
                  {icon:'📸',title:'Every run is reproducible',
                    body:'When you start a run in Fabric, the data config and model reference are snapshotted immediately. A year later you can go back to any run and see exactly what it was trained on, what columns were used, and with which parameters.',
                    tags:['Frozen snapshots','Audit trail','Reproducible']},
                  {icon:'🔐',title:'Your data doesn\'t leave.',
                    body:'Fabric sends the training script to your server. The data stays where it is. For teams in regulated industries, this isn\'t a nice-to-have - it\'s the only way training on sensitive data is possible.',
                    tags:['Healthcare','Finance','Legal','Defence']},
                  {icon:'📦',title:'Artifacts you can actually use',
                    body:'Every training run produces a versioned model artifact registered in Fabric\'s model registry. Domain pre-training outputs register automatically as base models for downstream runs. When you\'re ready, export to your own inference setup.',
                    tags:['Model registry','Versioned','Export-ready']},
                  {icon:'🔁',title:'Reuse what works',
                    body:'Feature configs and pipeline templates are saved and reused across projects. The preprocessing you built for one dataset can be applied to the next. The pipeline that worked for ITSM ticket classification can be adapted for legal document classification.',
                    tags:['Reusable configs','Pipeline templates']},
                  {icon:'🖥️',title:'Use the servers you already have',
                    body:'Register any SSH-accessible Linux server - a VM you\'re already paying for, on-prem hardware, or a dedicated GPU machine. You don\'t need new infrastructure to get started.',
                    tags:['CPU or GPU','Cloud or on-prem','Any Linux server']},
                  {icon:'📄',title:'Your documents are training data',
                    body:'Manuals, policies, support tickets, contracts. Upload them or point Fabric at a folder on your server, and they become a training corpus for models that speak your business. Merge hundreds of files into one corpus in a click.',
                    tags:['PDF and Word','Corpus builder','Domain models']},
                  {icon:'👁️',title:'Readable by more than just engineers',
                    body:'The runs page, audit log and metrics view are designed so that product managers, compliance teams and technical leadership can understand what was trained, when, and by whom - without reading code.',
                    tags:['Audit log','Metrics','Governance']},
                ].map(b => (
                  <div key={b.title} className="benefit-card">
                    <div className="benefit-icon">{b.icon}</div>
                    <h4>{b.title}</h4>
                    <p>{b.body}</p>
                    <div className="benefit-tags">
                      {b.tags.map(t => <span key={t} className="benefit-tag">{t}</span>)}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </section>

          <section className="section section-alt" id="usecases">
            <div className="container">
              <div className="section-header centered">
                <div className="eyebrow" style={{justifyContent:'center'}}>Use Cases</div>
                <h2>Where teams are using it</h2>
                <p style={{color:'#64748B',fontSize:12,marginTop:4}}>These are examples based on how teams in these sectors are applying ML training workflows. Your mileage will vary.</p>
              </div>
              <div className="usecase-grid">
                {[
                  {sector:'IT Service Management', icon:'🖥️', cases:[
                    'Classify and route incoming tickets to the right team automatically',
                    'Predict which tickets will breach SLA before they do',
                    'Estimate resolution time at ticket creation',
                    'Pre-train on your historical ticket corpus so the model understands your ITSM vocabulary before classification fine-tuning',
                  ]},
                  {sector:'Financial Services', icon:'🏦', cases:[
                    'Classify transactions without sending transaction data to a third-party API',
                    'Score credit or operational risk with regression models trained on your own data',
                    'Classify regulatory documents, contracts and correspondence',
                    'Maintain a full training audit trail for regulatory review',
                  ]},
                  {sector:'Healthcare', icon:'🏥', cases:[
                    'Classify clinical notes for triage, routing or coding support',
                    'Build patient outcome models on-premise without moving patient data',
                    'Extract conditions, medications and procedures from clinical text',
                    'Pre-train on clinical text so models understand medical language before task-specific fine-tuning',
                  ]},
                  {sector:'Legal & Compliance', icon:'⚖️', cases:[
                    'Classify contracts by type, risk level or jurisdiction',
                    'Extract clauses and named entities from legal documents',
                    'Route incoming regulatory correspondence',
                    'Maintain provenance records for any model used in a legal process',
                  ]},
                ].map(uc => (
                  <div key={uc.sector} className="usecase-card">
                    <div className="usecase-header">
                      <span className="usecase-icon">{uc.icon}</span>
                      <h4>{uc.sector}</h4>
                    </div>
                    <ul className="usecase-list">
                      {uc.cases.map(c => <li key={c}>{c}</li>)}
                    </ul>
                  </div>
                ))}
              </div>
            </div>
          </section>

          <section className="section" id="advantages">
            <div className="container">
              <div className="section-header centered">
                <div className="eyebrow" style={{justifyContent:'center'}}>How It Compares</div>
                <h2>What changes when you use Fabric</h2>
                <p>Fabric doesn't replace your data scientists. It gives them a shared environment where the work they do is tracked, reusable and explainable.</p>
              </div>
              <div className="adv-grid">
                {[
                  {icon:'📐', title:'Same process every time',
                    desc:'Every run goes through the same steps: pick a mode, select a model, configure the data, run. No bespoke setup per project. A new engineer can understand what\'s happening on day one.'},
                  {icon:'📊', title:'Metrics that follow the model',
                    desc:'Each run stores its own metrics, the data snapshot it was trained on, and the model version it produced. You can compare two runs side by side and see exactly what changed.'},
                  {icon:'🔁', title:'Build once, reuse across projects',
                    desc:'A feature config you built for one dataset can be reused on the next. A pipeline template that works for one team can be shared with another. The good work accumulates.'},
                  {icon:'📦', title:'Artifacts go somewhere useful',
                    desc:'Training runs produce versioned artifacts in the model registry. Domain pre-training outputs plug directly into downstream classification or NER runs as base models.'},
                  {icon:'👁️', title:'Non-engineers can read it',
                    desc:'The audit log, run history and metrics are designed for people who don\'t read code. Your compliance team doesn\'t need to open a notebook to understand what was trained.'},
                  {icon:'🔌', title:'You can extend anything',
                    desc:'Standard modes use built-in script generators. Override any stage with your own plugin - preprocessing, training, evaluation, post-processing. No lock-in on the execution side.'},
                ].map(a => (
                  <div key={a.title} className="adv-card">
                    <div style={{fontSize:22,marginBottom:10}}>{a.icon}</div>
                    <h5>{a.title}</h5>
                    <p>{a.desc}</p>
                  </div>
                ))}
              </div>

              <div className="notebook-compare">
                <div className="nc-header">
                  <h4>Versus notebook-based workflows</h4>
                  <p>Notebooks are good for exploration. For production training they create problems that are easy to ignore until they aren't.</p>
                </div>
                <div className="nc-grid">
                  {[
                    ['Notebooks', 'With Fabric'],
                    ['Each project has its own structure - or no structure', 'Same workflow for every run: mode → model → data → pipeline → artifact'],
                    ['Who ran the notebook last is the audit trail', 'User, data version, config, parameters - all logged automatically'],
                    ['Reproducing an old run means tracking down the right notebook and hoping the environment still works', 'Any run can be reproduced exactly from the stored snapshot'],
                    ['Model files land wherever they were saved', 'Every artifact is versioned and registered in the model registry'],
                    ['When the person who built it leaves, so does the context', 'The run history captures what decisions were made and when'],
                  ].map(([before, after], i) => (
                    i === 0 ? (
                      <div key="h" className="nc-row nc-row--header">
                        <div>{before}</div><div>{after}</div>
                      </div>
                    ) : (
                      <div key={before} className="nc-row">
                        <div className="nc-before"><span>-</span>{before}</div>
                        <div className="nc-after"><span>✓</span>{after}</div>
                      </div>
                    )
                  ))}
                </div>
              </div>
            </div>
          </section>
        </>
      )}


      {/* ════════════════════════════════════════════════════════════════════
          USER GUIDE / DOCS
      ════════════════════════════════════════════════════════════════════ */}
      </div>
      {/* ── CTA ── */}
      <section className="cta">
        <div className="container">
          <h2>{view === 'platform' ? 'Get a training run going.' : 'Want to talk through whether it fits?'}</h2>
          <p>{view === 'platform'
            ? 'Connect a server, upload a dataset, and run your first model. Takes under an hour to get to a result.'
            : 'Drop us a line. We\'re happy to talk through your setup and whether Fabric makes sense for it.'
          }</p>
          <div style={{display:'flex', gap:12, justifyContent:'center', flexWrap:'wrap'}}>
            <button className="btn btn-lg btn-cta-white" onClick={() => setShowAccess(true)}>Request Access →</button>
            <button className="btn btn-lg btn-cta-ghost" onClick={onGetStarted}>Login</button>
            <a href="mailto:hello@aiiop.ai" className="btn btn-lg btn-cta-ghost">Contact Us</a>
          </div>
        </div>
      </section>

      {/* ── Footer ── */}
      <footer className="footer">
        <div className="container">
          <div className="footer-grid">
            <div className="footer-brand">
              <h3><span>aiiop</span> Fabric</h3>
              <p>ML training on your own infrastructure. Traceable, repeatable, exportable.</p>
              <p style={{marginTop:12, fontFamily:'var(--mono)', fontSize:'9.5px', color:'#334155'}}>Part of the aiiop platform · by IDBS</p>
            </div>
            <div className="footer-col">
              <h4>Platform</h4>
              <ul>
                <li><button className="footer-link" onClick={() => switchView('platform')}>How It Works</button></li>
                <li><button className="footer-link" onClick={onGoToDocs}>User Guide</button></li>
                <li><button className="footer-link" onClick={() => scrollToId('platform', 'purposes')}>Training Modes</button></li>
                <li><button className="footer-link" onClick={() => scrollToId('platform', 'pipeline')}>Pipelines</button></li>
                <li><button className="footer-link" onClick={() => scrollToId('platform', 'governance')}>Audit & Access</button></li>
              </ul>
            </div>
            <div className="footer-col">
              <h4>Why Fabric</h4>
              <ul>
                <li><button className="footer-link" onClick={() => switchView('why')}>The Problem</button></li>
                <li><button className="footer-link" onClick={() => scrollToId('why', 'usecases')}>Use Cases</button></li>
                <li><button className="footer-link" onClick={() => scrollToId('why', 'advantages')}>How It Compares</button></li>
              </ul>
            </div>
            <div className="footer-col">
              <h4>Contact</h4>
              <ul>
                <li><a href="mailto:hello@aiiop.ai">hello@aiiop.ai</a></li>
                <li><a href="mailto:sales@aiiop.ai">sales@aiiop.ai</a></li>
                <li><a href="https://www.aiiop.ai" target="_blank" rel="noopener noreferrer">www.aiiop.ai</a></li>
              </ul>
            </div>
          </div>
          <div className="footer-bottom">
            <p>© 2025-2026 Incisive Design and Business Solutions. All rights reserved.</p>
            <span className="footer-ver"><b>v2.0</b> · aiiop Fabric</span>
          </div>
        </div>
      </footer>
      {showAccess && <RequestAccessModal onClose={() => setShowAccess(false)} />}
    </div>
  );
}
